import React, { useCallback, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { type FormikHelpers, type FormikProps } from 'formik'
import { intl } from 'lang/intl'
import { FormattedMessage } from 'react-intl'

import { TtButton, TtFieldset, TtIcon } from '@23/react'

import { useEditActionCardMutation } from '../../../../../../ActionCards/blocs/useActionCards'
import { editActionCard } from '../../../../../../firebase/hooks/useTray'
import { type TrayCardAction } from '../../../../../../firebase/model/tray-cards/types/action-card-model'
import { getValidationErrors } from '../../../../../../global/api/api'
import TtFormik from '../../../../../../global/formik-forms'
import { getLiveId } from '../../../../../../global/blocs/roomInfo'
import { uploadActionCardImage } from '../../../api/upload'
import useObjectUrl from '../../../../../../global/hooks/useObjectUrl'

import { ActionCardField } from './Options/ActionCardField'
import { useOpenDeleteActionCardFieldConfirmationModal } from './DeleteFieldConfirmation/DeleteFieldConfirmation'

import './ActionCardEditForm.scss'

interface PreviewImageProps {
  image: string | File | undefined
}

const PreviewImage = ({ image }: PreviewImageProps) => {
  const fileObjectUrl = useObjectUrl(image instanceof File ? image : undefined)
  const imageURL = typeof image === 'string' ? image : fileObjectUrl

  return (
    <img
      className="action-card-preview-image"
      src={imageURL}
      alt={intl.formatMessage({ defaultMessage: 'Action Card Thumbnail' })}
      slot="dropzone"
    />
  )
}

type FormAdditionalFields = {
  name: string
  type: 'text-short' | 'text-long' | 'check'
  label: string
  fieldId?: number
}[]

type ActionCardFormValues = {
  title?: string
  description?: string
  image?: FileList | string
  submitLabel?: string
  submitTargetUrl?: string
}

interface ActionCardFormProps {
  card: TrayCardAction
  onSubmit: () => void
  onCancel: () => void
}

const ActionCardEditForm = ({
  card,
  onSubmit,
  onCancel,
}: ActionCardFormProps) => {
  const { mutateAsync: updateActionCard } = useEditActionCardMutation(
    card.actionCardId
  )

  const mapTrayCardActionOptionsToFormValues = (
    options: TrayCardAction['options']
  ): FormAdditionalFields => {
    return (
      options?.map((option) => ({
        name: `${option.id}`,
        type: option.type,
        label: option.label,
        fieldId: option.id,
      })) ?? [{ name: uuidv4(), type: 'text-short', label: '' }]
    )
  }

  const initialFields = mapTrayCardActionOptionsToFormValues(card.options)
  const [fields, setFields] = useState<FormAdditionalFields>(initialFields)

  const [showButtonLinkInput, setShowButtonLinkInput] = useState(
    !!card.cta?.url
  )

  const openDeleteFieldConfirmationModal =
    useOpenDeleteActionCardFieldConfirmationModal()

  const onAddFieldClicked = () => {
    setFields((prevFields) => [
      ...prevFields,
      {
        name: uuidv4(),
        type: 'text-short',
        label: '',
      },
    ])
  }

  const onRemoveFieldClicked = useCallback(
    async (name: string) => {
      try {
        const item = fields.find((field) => field.name === name)

        if (item?.label) {
          const result = await openDeleteFieldConfirmationModal()
          if (!result) return
        }

        setFields((prevFields) =>
          prevFields.filter((field) => field.name !== name)
        )
      } catch (e) {
        // TODO: Handle cancellation or error
      }
    },
    [fields, openDeleteFieldConfirmationModal]
  )

  const onAdditionalOptionChange = (event: CustomEvent, name: string) => {
    const { value, type } = event.detail
    setFields((prevFields) =>
      prevFields.map((prevField) =>
        prevField.name === name
          ? { ...prevField, label: value, type }
          : prevField
      )
    )
  }

  const sensors = useSensors(useSensor(PointerSensor))
  const [isCurrentlyDragging, setIsCurrentlyDragging] = useState(false)

  const onDragStartHandler = useCallback(() => {
    setIsCurrentlyDragging(true)
  }, [setIsCurrentlyDragging])

  const onDragEndHandler = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      setIsCurrentlyDragging(false)

      if (fields && over && active.id !== over.id) {
        const oldIndex = fields.findIndex((field) => field.name === active.id)
        const newIndex = fields.findIndex((field) => field.name === over.id)

        setFields(arrayMove(fields, oldIndex, newIndex))
      }
    },
    [fields]
  )

  const formikRef = useRef<FormikProps<ActionCardFormValues>>(null)

  const initialValues: ActionCardFormValues = {
    title: card.title,
    description: card.description,
    image: card.image,
    submitLabel: card.cta?.text,
    submitTargetUrl: card.cta?.url,
  }

  const resetImageHandler = useCallback(
    (formik: FormikHelpers<ActionCardFormValues>) => {
      formik.setFieldValue('image', undefined)
    },
    []
  )

  const onSubmitHandler = useCallback(
    async (
      formik: FormikHelpers<ActionCardFormValues>,
      values: ActionCardFormValues,
      additionalFields: FormAdditionalFields
    ) => {
      try {
        let imageUrl: string | null = null
        // upload image if provided
        if (values.image && values.image instanceof FileList) {
          const file = Array.from(values.image).at(0)
          if (!file) return
          imageUrl = (await uploadActionCardImage(`${getLiveId()}`, file)).url
        } else {
          imageUrl = values.image ?? null
        }

        const actionCard = await updateActionCard({
          title: values.title ?? null,
          description: values.description ?? null,
          imageUrl,
          submitLabel: values.submitLabel || null,
          submitTargetUrl: values.submitTargetUrl || null,
          fields: additionalFields
            .map(
              (field) =>
                ({
                  type: field.type,
                  label: field.label,
                  fieldId: field.fieldId ?? null,
                }) as const
            )
            .filter((field) => !!field.label.trim()),
        })

        if (!actionCard) return

        await editActionCard(card.id, actionCard)

        onSubmit()
      } catch (error) {
        const validationErrors = getValidationErrors(error)
        if (!validationErrors) return
        Object.entries(validationErrors).forEach(([fieldName, errors]) => {
          const errorMessage = errors.at(0)?.message
          if (!errorMessage) return
          formik.setFieldError(
            fieldName,
            errorMessage
              .replace('submitLabel', 'Text')
              .replace('submitTargetUrl', 'URL')
          )
        })
      }
    },
    [card.id, onSubmit, updateActionCard]
  )

  return (
    <TtFormik.Form<ActionCardFormValues>
      innerRef={formikRef}
      label={intl.formatMessage({ defaultMessage: 'Configure Action Card' })}
      className="action-card-form"
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, formikHelpers) =>
        onSubmitHandler(formikHelpers, values, fields)
      }
      novalidate
    >
      {(formik) => (
        <>
          <TtFormik.TextField
            size="small"
            name="title"
            label={intl.formatMessage({
              defaultMessage: 'Title of the action card',
            })}
            hideLabel
            placeholder={intl.formatMessage({ defaultMessage: 'Add a title' })}
          />

          <TtFormik.TextArea
            name="description"
            label={intl.formatMessage({
              defaultMessage: 'Description of the action card',
            })}
            hideLabel
            placeholder={intl.formatMessage({
              defaultMessage: 'Add a description',
            })}
            lines={3}
          />

          {formik.values.image ? (
            <div className="action-card-image-upload">
              <TtFormik.FileInput
                name="image"
                label={intl.formatMessage({
                  defaultMessage: 'Image of the action card',
                })}
                hideLabel
                accept="image/*"
              >
                <PreviewImage
                  image={
                    formik.values.image instanceof FileList &&
                    formik.values.image.length > 0
                      ? formik.values.image[0]
                      : card.image
                  }
                />
              </TtFormik.FileInput>
              <TtButton
                className="action-card-remove-image-btn"
                variant="light"
                color="secondary"
                size="x-small"
                circle
                label={intl.formatMessage({
                  defaultMessage: 'Remove image',
                })}
                onClick={() => resetImageHandler(formik)}
              >
                <TtIcon library="one" name="close" />
              </TtButton>
            </div>
          ) : (
            <TtFormik.FileInput
              name="image"
              label={intl.formatMessage({
                defaultMessage: 'Image of the action card',
              })}
              hideLabel
              accept="image/*"
            />
          )}

          <TtFieldset
            direction="vertical"
            label={intl.formatMessage({ defaultMessage: 'Fields' })}
          >
            <div className="action-card-fields-container">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEndHandler}
                onDragStart={onDragStartHandler}
                onDragCancel={() => setIsCurrentlyDragging(false)}
              >
                <SortableContext
                  items={
                    fields.map((field) => ({ ...field, id: field.name })) ?? []
                  }
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((field) => (
                    <ActionCardField
                      key={field.name}
                      name={field.name}
                      type={field.type}
                      value={field.label}
                      isCurrentlyDragging={isCurrentlyDragging}
                      onRemoveFieldClicked={onRemoveFieldClicked}
                      onAdditionalOptionChange={onAdditionalOptionChange}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <TtButton
                id="add-choice-btn"
                variant="outline"
                color="secondary"
                size="small"
                label={intl.formatMessage({
                  defaultMessage: 'Add a new field',
                })}
                onClick={onAddFieldClicked}
              >
                <TtIcon slot="prefix" library="one" name="plus" />
                <FormattedMessage defaultMessage="Add field" />
              </TtButton>
            </div>
          </TtFieldset>

          <TtFieldset
            direction="horizontal"
            label={intl.formatMessage({ defaultMessage: 'Button' })}
          >
            <TtFormik.TextField
              size="small"
              name="submitLabel"
              label={intl.formatMessage({ defaultMessage: 'Button text' })}
              hideLabel
              placeholder={intl.formatMessage({
                defaultMessage: 'Button text',
              })}
            />
            {showButtonLinkInput ? (
              <TtFormik.TextField
                size="small"
                name="submitTargetUrl"
                label={intl.formatMessage({ defaultMessage: 'Button link' })}
                hideLabel
                placeholder={intl.formatMessage({
                  defaultMessage: 'Button link',
                })}
              />
            ) : (
              <TtButton
                label={intl.formatMessage({
                  defaultMessage: 'Add button link',
                })}
                color="secondary"
                variant="ghost"
                size="small"
                className="action-card-add-link-btn"
                onClick={() => setShowButtonLinkInput(true)}
              >
                <TtIcon library="one" name="link" />
              </TtButton>
            )}
          </TtFieldset>
          <TtFormik.SubmitButton
            color="secondary"
            size="small"
            variant="default"
            label={intl.formatMessage({ defaultMessage: 'Save changes' })}
            disabled={false}
            pill
            onCancel={onCancel}
          >
            <FormattedMessage defaultMessage="Save" />
          </TtFormik.SubmitButton>
        </>
      )}
    </TtFormik.Form>
  )
}

const ActionCardFormMemoized = React.memo(ActionCardEditForm)

export {
  type ActionCardFormValues,
  type FormAdditionalFields,
  ActionCardFormMemoized as ActionCardEditForm,
}
